/**
 * AUDIENCE SERVICES — powers the "Who we serve" switcher on the homepage.
 *
 * ✏️ HOW TO UPDATE (for Julio & Nick):
 *   - Each audience has a tab label, a one-line intro, bullet services, and a button.
 *   - Edit text in BOTH en and es so the Spanish site stays complete.
 *   - `cta.to` is the page the button opens (e.g. "/pickup", "/business-quote").
 * Content here mirrors what the site already promised — update when services change.
 */

export type Audience = {
  id: "residents" | "buildings" | "businesses" | "community";
  icon: string;
  tab: { en: string; es: string };
  title: { en: string; es: string };
  intro: { en: string; es: string };
  points: { en: string; es: string }[];
  cta: { to: string; label: { en: string; es: string } };
};

export const AUDIENCES: Audience[] = [
  {
    id: "residents",
    icon: "🏠",
    tab: { en: "Residents", es: "Residentes" },
    title: { en: "Free pickups, right from your door", es: "Recogidas gratis, directo en tu puerta" },
    intro: {
      en: "Electronics, clothing, furniture and household goods — we come to you, free, anywhere in the five boroughs.",
      es: "Electrónicos, ropa, muebles y artículos del hogar — vamos a ti, gratis, en los cinco condados.",
    },
    points: [
      { en: "📅 Pick any date & time that works for you", es: "📅 Elige la fecha y hora que te convenga" },
      { en: "🧾 Tax-deductible donation receipts available", es: "🧾 Recibos de donación deducibles de impuestos" },
      { en: "🎁 Earn EcoPoints on every pickup", es: "🎁 Gana EcoPoints en cada recogida" },
      { en: "❤️ Working items are refurbished and donated to neighbors", es: "❤️ Lo que funciona se repara y se dona a vecinos" },
    ],
    cta: { to: "/pickup", label: { en: "Schedule a Free Pickup →", es: "Agenda tu Recogida Gratis →" } },
  },
  {
    id: "buildings",
    icon: "🏬",
    tab: { en: "Buildings", es: "Edificios" },
    title: { en: "A cleaner building, a happier lobby", es: "Un edificio más limpio, un lobby más feliz" },
    intro: {
      en: "For property and building managers: recurring pickups and cleanouts that keep your building clutter-free.",
      es: "Para administradores de edificios: recogidas recurrentes y vaciados que mantienen tu edificio sin trastos.",
    },
    points: [
      { en: "🔁 Recurring recycling days as a perk for residents", es: "🔁 Días de reciclaje recurrentes como beneficio para residentes" },
      { en: "📦 Move-out and basement cleanouts", es: "📦 Vaciados de mudanzas y sótanos" },
      { en: "🚐 We work around your building's schedule", es: "🚐 Nos adaptamos al horario de tu edificio" },
      { en: "🧾 Documentation for your management office", es: "🧾 Documentación para tu oficina de administración" },
    ],
    cta: { to: "/business-quote", label: { en: "Get a Building Quote →", es: "Cotización para tu Edificio →" } },
  },
  {
    id: "businesses",
    icon: "💼",
    tab: { en: "Businesses", es: "Negocios" },
    title: { en: "Cleanouts handled like a real partner", es: "Vaciados manejados como un socio de verdad" },
    intro: {
      en: "Offices, restaurants, retail: bulk e-waste, cleanouts and data-safe electronics recovery — with paperwork.",
      es: "Oficinas, restaurantes, tiendas: e-waste en volumen, vaciados y recuperación segura de electrónicos — con papeleo.",
    },
    points: [
      { en: "🔒 Secure data destruction on drives and devices", es: "🔒 Destrucción segura de datos en discos y equipos" },
      { en: "🏢 Full office cleanouts, minimal disruption", es: "🏢 Vaciados completos de oficina, sin interrumpir tu operación" },
      { en: "📊 ESG / CSR impact reports for your sustainability story", es: "📊 Reportes de impacto ESG / CSR para tu historia de sostenibilidad" },
      { en: "🧾 Receipts and donation acknowledgments", es: "🧾 Recibos y constancias de donación" },
    ],
    cta: { to: "/business-quote", label: { en: "Get a Free Business Quote →", es: "Cotización Gratis para tu Negocio →" } },
  },
  {
    id: "community",
    icon: "🤝",
    tab: { en: "Community", es: "Comunidad" },
    title: { en: "Schools, shelters & neighbors", es: "Escuelas, refugios y vecinos" },
    intro: {
      en: "We team up with schools, nonprofits and shelters to run drives and route donations to people who need them.",
      es: "Nos unimos con escuelas, organizaciones y refugios para hacer jornadas y llevar donaciones a quien las necesita.",
    },
    points: [
      { en: "🏫 Host an e-waste or clothing drive — we handle logistics", es: "🏫 Organiza una jornada de e-waste o ropa — nosotros hacemos la logística" },
      { en: "💻 Refurbished electronics for the people you serve", es: "💻 Electrónicos reparados para la gente que sirves" },
      { en: "🎓 Hands-on recycling education workshops", es: "🎓 Talleres prácticos de educación en reciclaje" },
      { en: "🧭 Free help finding jobs, training & resources", es: "🧭 Ayuda gratis para encontrar trabajo, entrenamiento y recursos" },
    ],
    cta: { to: "/contact", label: { en: "Become a Partner →", es: "Hazte Aliado →" } },
  },
];
